import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

/*
 * CursorField — a subtle, brand-matched particle background (à la antigravity.google).
 * A field of small white / soft-green dots that:
 *   - drift slowly on their own (ambient life),
 *   - parallax gently with the cursor (depth), and
 *   - softly repel away from the cursor when it gets near.
 * [STARDUST 2026-09-28] Plus a stardust trail: moving the cursor sheds small sparks (same
 * palette) that drift and fade out in about a second. The repel field above is unchanged.
 *
 * Canvas + requestAnimationFrame (no library). Honors prefers-reduced-motion
 * (renders a static field, no interaction, no trail). Fills its positioned parent.
 */

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  z-index: 0;
`;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  depth: number; // parallax weight (bigger/closer dots move more)
  color: string;
  ox: number; // eased x offset (repel + parallax)
  oy: number;
}

const COLORS = [
  'rgba(255,255,255,OP)', // white
  'rgba(209,255,226,OP)', // soft green-white
  'rgba(134,239,172,OP)', // light green
];

const REPEL_RADIUS = 130;
const REPEL_FORCE = 26;

// [STARDUST 2026-09-28] Cursor trail sparks.
interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  rgb: string;
  life: number; // 1 → 0
}

const SPARK_RGB = ['255,255,255', '209,255,226', '134,239,172']; // same palette as COLORS
const SPARK_STEP = 7; // px of cursor travel per spark
const SPARK_MAX_PER_FRAME = 4;
const SPARK_DECAY = 0.016; // life lost per frame (~1 s at 60 fps)
const SPARK_CAP = 220;

const CursorField: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const mouse = useRef({ x: 0, y: 0, tx: 0, ty: 0, active: false });
  const sizeRef = useRef({ w: 0, h: 0 });
  const sparksRef = useRef<Spark[]>([]);
  const lastSpawn = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const build = () => {
      const { w, h } = sizeRef.current;
      // ~1 dot per 13k px², clamped, so density scales with screen size.
      const count = Math.min(190, Math.max(60, Math.round((w * h) / 13000)));
      const arr: Particle[] = [];
      for (let i = 0; i < count; i++) {
        const baseOp = (0.16 + Math.random() * 0.42).toFixed(2);
        const color = COLORS[Math.floor(Math.random() * COLORS.length)].replace('OP', baseOp);
        arr.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.12,
          vy: (Math.random() - 0.5) * 0.12,
          r: 0.8 + Math.random() * 1.8,
          depth: 0.3 + Math.random() * 1.2,
          color,
          ox: 0,
          oy: 0,
        });
      }
      particlesRef.current = arr;
    };

    const drawStatic = () => {
      const { w, h } = sizeRef.current;
      ctx.clearRect(0, 0, w, h);
      for (const p of particlesRef.current) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      }
    };

    const resize = () => {
      const parent = canvas.parentElement;
      const w = parent ? parent.clientWidth : window.innerWidth;
      const h = parent ? parent.clientHeight : window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      sizeRef.current = { w, h };
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
      sparksRef.current = [];
      // Default cursor to center so parallax is neutral until the mouse moves.
      mouse.current.x = mouse.current.tx = w / 2;
      mouse.current.y = mouse.current.ty = h / 2;
      if (reduce) drawStatic();
    };

    const frame = () => {
      const { w, h } = sizeRef.current;
      ctx.clearRect(0, 0, w, h);

      // Ease the actual cursor toward its target for buttery motion.
      mouse.current.x += (mouse.current.tx - mouse.current.x) * 0.08;
      mouse.current.y += (mouse.current.ty - mouse.current.y) * 0.08;

      const pxBase = mouse.current.x - w / 2;
      const pyBase = mouse.current.y - h / 2;

      for (const p of particlesRef.current) {
        // ambient drift + edge wrap
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -6) p.x = w + 6;
        else if (p.x > w + 6) p.x = -6;
        if (p.y < -6) p.y = h + 6;
        else if (p.y > h + 6) p.y = -6;

        // soft repel from cursor
        let rx = 0;
        let ry = 0;
        if (mouse.current.active) {
          const dx = p.x - mouse.current.x;
          const dy = p.y - mouse.current.y;
          const dist = Math.hypot(dx, dy);
          if (dist < REPEL_RADIUS && dist > 0.01) {
            const f = (1 - dist / REPEL_RADIUS) * REPEL_FORCE;
            rx = (dx / dist) * f;
            ry = (dy / dist) * f;
          }
        }

        // gentle parallax by depth
        const px = pxBase * 0.02 * p.depth;
        const py = pyBase * 0.02 * p.depth;

        // ease combined offset
        p.ox += (rx + px - p.ox) * 0.12;
        p.oy += (ry + py - p.oy) * 0.12;

        ctx.beginPath();
        ctx.arc(p.x + p.ox, p.y + p.oy, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      }

      // [STARDUST 2026-09-28] Shed sparks from the real pointer position (not the eased one, which
      // lags behind), one per SPARK_STEP px of travel; then drift, shrink and fade each spark.
      const sparks = sparksRef.current;
      if (mouse.current.active) {
        const moved = Math.hypot(mouse.current.tx - lastSpawn.current.x, mouse.current.ty - lastSpawn.current.y);
        if (moved > SPARK_STEP) {
          const n = Math.min(SPARK_MAX_PER_FRAME, Math.floor(moved / SPARK_STEP));
          for (let i = 0; i < n; i++) {
            sparks.push({
              x: mouse.current.tx + (Math.random() - 0.5) * 8,
              y: mouse.current.ty + (Math.random() - 0.5) * 8,
              vx: (Math.random() - 0.5) * 1,
              vy: -0.7 + Math.random() * 0.9, // slight upward float
              r: 0.8 + Math.random() * 1.2,
              rgb: SPARK_RGB[Math.floor(Math.random() * SPARK_RGB.length)],
              life: 1,
            });
          }
          lastSpawn.current.x = mouse.current.tx;
          lastSpawn.current.y = mouse.current.ty;
        }
      }
      if (sparks.length > SPARK_CAP) sparks.splice(0, sparks.length - SPARK_CAP);
      let alive = 0;
      for (const s of sparks) {
        s.life -= SPARK_DECAY;
        if (s.life <= 0) continue;
        s.x += s.vx;
        s.y += s.vy;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * s.life, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.rgb},${(0.7 * s.life).toFixed(3)})`;
        ctx.fill();
        sparks[alive++] = s;
      }
      sparks.length = alive;

      rafRef.current = requestAnimationFrame(frame);
    };

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      // [APP-FIT 2026-10-26] Screen px → the canvas's own px (the dots' space); they differ under the app zoom (utils/appZoom).
      const k = rect.width > 0 ? canvas.clientWidth / rect.width : 1;
      mouse.current.tx = (e.clientX - rect.left) * k;
      mouse.current.ty = (e.clientY - rect.top) * k;
      // Coming back in from outside: start the trail here, not with a streak from the old spot.
      if (!mouse.current.active) {
        lastSpawn.current.x = mouse.current.tx;
        lastSpawn.current.y = mouse.current.ty;
      }
      mouse.current.active = true;
    };
    const onLeave = () => {
      mouse.current.active = false;
    };

    resize();
    window.addEventListener('resize', resize);

    if (!reduce) {
      window.addEventListener('mousemove', onMove);
      document.addEventListener('mouseleave', onLeave);
      rafRef.current = requestAnimationFrame(frame);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return <Canvas ref={canvasRef} aria-hidden="true" />;
};

export default CursorField;
